import { normalizeSearchText } from '@gozoki/tools'
import type { AppSelectOption } from '~/components/new/app-ui/app-select'

/** Logique pure des filtres de la page Logistique : testée par `logistics-filter-options.test.mjs`. */

/** État de la requête des options, au sens de react-query (`query.status`). */
export type OptionsStatus = 'pending' | 'error' | 'success'

/**
 * Options d'un filtre dont la valeur vient de l'URL. Une valeur absente des options reste visible
 * et retirable : ajoutée sous `unknownLabel`, qui reçoit l'état — `success` (options chargées, la
 * valeur n'y est pas : atelier supprimé…) ou `error` (options indisponibles, rien n'est dit de la
 * valeur). Pendant le chargement, aucune valeur (placeholder) plutôt qu'un id brut.
 */
export const filterOptionsWithCurrent = <TValue extends string | number>(
  options: AppSelectOption<TValue>[],
  current: TValue | undefined,
  status: OptionsStatus,
  unknownLabel: (value: TValue, status: Exclude<OptionsStatus, 'pending'>) => string,
): { options: AppSelectOption<TValue>[]; value: TValue | null } => {
  if (current === undefined) return { options, value: null }
  if (options.some((option) => option.value === current)) return { options, value: current }
  if (status === 'pending') return { options, value: null }
  return {
    options: [...options, { label: unknownLabel(current, status), value: current }],
    value: current,
  }
}

/** État combiné de plusieurs lectures alimentant un même filtre (erreur, sinon chargement). */
export const combinedStatus = (statuses: readonly OptionsStatus[]): OptionsStatus => {
  if (statuses.includes('error')) return 'error'
  if (statuses.includes('pending')) return 'pending'
  return 'success'
}

/** Texte saisi ou reçu, rogné : `undefined` s'il est vide ou absent. */
export const optionalText = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim() ?? ''
  return trimmed === '' ? undefined : trimmed
}

/** Même texte rogné, `null` s'il est vide ou absent (valeur d'API : `null` efface le champ). */
export const textOrNull = (value: string | null | undefined): string | null =>
  optionalText(value) ?? null

/**
 * Texte comparable par la recherche d'une liste Logistique : sans casse, accents ni séparateurs
 * (« GP-482 » trouve la plaque `GP482LU`, « chevilly larue » trouve « Chevilly-Larue »).
 */
export const searchableText = (value: string): string =>
  normalizeSearchText(value).replace(/[^a-z0-9]/g, '')

/**
 * Ordre des listes Logistique : le nom affiché (`name` par défaut), sans casse, les nombres dans
 * leur ordre (« Hub 2 » avant « Hub 10 »).
 */
export function sortByName<T extends { name: string }>(rows: readonly T[]): T[]
export function sortByName<T>(rows: readonly T[], nameOf: (row: T) => string): T[]
export function sortByName<T>(
  rows: readonly T[],
  nameOf: (row: T) => string = (row) => (row as { name: string }).name,
): T[] {
  return [...rows].sort((a, b) =>
    nameOf(a).localeCompare(nameOf(b), 'fr', { sensitivity: 'base', numeric: true }),
  )
}

/**
 * Recherche d'une liste Logistique : chaque mot de la saisie doit se trouver dans l'un des champs,
 * dans n'importe quel ordre (« benauge bordeaux » trouve « 12 rue de la Benauge, 33100 Bordeaux »).
 * Saisie vide : tout passe.
 */
export const matchesSearch = (
  query: string | undefined,
  fields: readonly (string | null)[],
): boolean => {
  const words = (query ?? '')
    .split(/\s+/)
    .map(searchableText)
    .filter((word) => word !== '')
  if (words.length === 0) return true
  const texts = fields.flatMap((field) => (field === null ? [] : [searchableText(field)]))
  return words.every((word) => texts.some((text) => text.includes(word)))
}

/**
 * Compteurs « Disponibilité » d'un en-tête de liste (camions, collaborateurs). Un état inconnu
 * (`null`) n'est compté nulle part.
 */
export const availabilityCounts = (
  rows: readonly { isActive: boolean | null }[],
): { available: number; unavailable: number } => ({
  available: rows.filter((row) => row.isActive === true).length,
  unavailable: rows.filter((row) => row.isActive === false).length,
})
